<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{
  checkinIso: string | null;
  guests: number;
}>();

const { t, locale } = useI18n();
const { trackEvent } = useAnalytics();
const config = useRuntimeConfig();

const phone = computed(() => (config.public.whatsappNumber as string) || '+995505556117');
const email = computed(() => (config.public.contactEmail as string) || 'info@georgiadream.ge');

const whatsappUrl = computed(() => {
  const digits = phone.value.replace(/\D/g, '');
  const dateLabel = props.checkinIso ?? '—';
  const text =
    locale.value === 'ru'
      ? `Здравствуйте! Хочу уточнить тариф для долгого проживания от ${dateLabel}, ${props.guests} гост.`
      : `Hi! I'd like to enquire about a long stay starting ${dateLabel}, ${props.guests} guest(s).`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
});

const emailUrl = computed(() => {
  const subject =
    locale.value === 'ru'
      ? 'Запрос: долгое проживание (30+ ночей)'
      : 'Inquiry: long stay (30+ nights)';
  const body =
    locale.value === 'ru'
      ? `Дата заезда: ${props.checkinIso ?? '—'}\nГостей: ${props.guests}\n\nХочу обсудить условия на проживание от месяца.`
      : `Check-in: ${props.checkinIso ?? '—'}\nGuests: ${props.guests}\n\nI'd like to discuss conditions for a month-long stay.`;
  return `mailto:${email.value}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
});

function onWhatsappClick() {
  trackEvent('prices_calculator_long_stay_contact', {
    channel: 'whatsapp',
    from: props.checkinIso,
    guests: props.guests,
    nights: 30,
  });
}

function onEmailClick() {
  trackEvent('prices_calculator_long_stay_contact', {
    channel: 'email',
    from: props.checkinIso,
    guests: props.guests,
    nights: 30,
  });
}
</script>

<template>
  <div class="border-t border-slate-200 dark:border-slate-700 bg-amber-50/50 dark:bg-amber-500/5 p-6 md:p-8 text-center">
    <div class="flex items-center justify-center gap-3 font-body text-xs uppercase tracking-[0.22em] text-amber-700 dark:text-amber-300">
      <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
      <span>{{ t('prices.calculator.longStay.eyebrow') }}</span>
      <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
    </div>
    <h3 class="mt-3 font-display italic text-2xl md:text-3xl text-slate-900 dark:text-white">
      {{ t('prices.calculator.longStay.title') }}
    </h3>
    <p class="mt-3 font-body text-sm md:text-base text-slate-700 dark:text-slate-200 max-w-xl mx-auto">
      {{ t('prices.calculator.longStay.body') }}
    </p>
    <div class="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
      <a
        :href="whatsappUrl"
        target="_blank"
        rel="noopener"
        class="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-600 px-6 py-3 font-body text-sm font-semibold uppercase tracking-[0.14em] text-white transition"
        @click="onWhatsappClick"
      >
        <Icon name="lucide:message-circle" class="size-4" aria-hidden="true" />
        {{ t('prices.calculator.longStay.whatsappCta') }}
      </a>
      <a
        :href="emailUrl"
        class="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 dark:border-slate-600 px-6 py-3 font-body text-sm font-semibold uppercase tracking-[0.14em] text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
        @click="onEmailClick"
      >
        <Icon name="lucide:mail" class="size-4" aria-hidden="true" />
        {{ t('prices.calculator.longStay.emailCta') }}
      </a>
    </div>
  </div>
</template>
